import {
  useProductCoolingRegime,
  useUpdateCoolingRegimeProductTemperatures,
  useUpdateCoolingRegimeProductPriorities,
  useDeleteCoolingRegimeProduct,
  useCreateCoolingRegimeProduct,
} from "../../hooks/useProductCoolingRegime";
import {
  EditableCoolingRegimeDetails,
  type TemperatureUpdates,
  type PriorityUpdates,
} from "../CoolingRegime/EditableCoolingRegimeDetails";
import { useProductDetails } from "../../hooks/useProductDetails";
import { Box, CircularProgress } from "@mui/material";
import { ProductCoolingRegimeCreateDialog } from "../CoolingRegime/ProductCoolingRegimeCreateDialog";
import { useState, useMemo } from "react";
import { EmptyState } from "../EmptyState";
import { useFeatureFlag, Feature } from "../../hooks/useFeatureFlag";

interface Props {
  productId: string;
}

export const CoolingRegimesTabEditable: React.FC<Props> = ({ productId }) => {
const {
  data: coolingRegimes,
  isLoading,
  isError,
} = useProductCoolingRegime(productId);

const { data: productDetails } = useProductDetails(productId);
const productGroupCode = productDetails?.productGroup?.code as number | undefined;

const updateTemperaturesMutation =
  useUpdateCoolingRegimeProductTemperatures();
const updatePrioritiesMutation = useUpdateCoolingRegimeProductPriorities();
const deleteMutation = useDeleteCoolingRegimeProduct();
const createMutation = useCreateCoolingRegimeProduct();

// Check permissions
const canMutateProduct = useFeatureFlag(Feature.MutateCoolingRegimeProduct);
const canMutateTemperature = useFeatureFlag(Feature.MutateCoolingRegimeTemperature);
const canMutatePriority = useFeatureFlag(Feature.MutateCoolingRegimePriority);

  const firstRegime = coolingRegimes?.[0];
  const coolingRegimeProductGroupId = (firstRegime as any)?.coolingRegimeProductGroupId as number | undefined;
  const hasProductRegime = typeof (firstRegime as any)?.coolingRegimeProductId === 'number' && (firstRegime as any).coolingRegimeProductId! > 0;
  const productCode = (firstRegime as any)?.productCode as number | undefined;

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const openCreate = () => setIsCreateOpen(true);
  const closeCreate = () => setIsCreateOpen(false);

  // useMemo must be called before any early returns to maintain hook order
  const initialCreateValues = useMemo(() => {
    if (!firstRegime) return undefined;
    return {
      shortTermMain: firstRegime.coolingTemperatureCodeShortTerm ?? null,
      longTermMain: firstRegime.coolingTemperatureCodeLongTerm ?? null,
      shortTermDisplacementMain: firstRegime.coolingTemperatureCodeShortTermDisplacement ?? null,
      longTermDisplacementMain: firstRegime.coolingTemperatureCodeLongTermDisplacement ?? null,
      shortTermPriority: firstRegime.coolingPriorityCodeShortTerm ?? null,
      longTermPriority: firstRegime.coolingPriorityCodeLongTerm ?? null,
      relativeHumidity: ((): 'J' | 'N' | null => {
        const raw = String(firstRegime.relativeHumidityIndication ?? '').toUpperCase();
        if (raw === 'J' || raw === 'Y') return 'J';
        if (raw === 'N') return 'N';
        return null;
      })(),
    };
  }, [firstRegime]);

  // Show loading state while fetching data
  if (isLoading) {
    return (
      <Box p={2} display="flex" justifyContent="center">
        <CircularProgress />
      </Box>
    );
  }

  // Show empty state when no cooling regimes are present
  if (!coolingRegimes || coolingRegimes.length === 0) {
    const message = typeof productGroupCode === "number"
      ? `Geen gegevens gevonden voor productgroepcode ${productGroupCode}.`
      : "Geen gegevens gevonden.";
    return (
      <Box p={2}>
        <EmptyState title="Geen gegevens gevonden" message={message} />
      </Box>
    );
  }

  const handleSaveTemperature = async (
    field: keyof TemperatureUpdates,
    value: number | null,
  ) => {
    if (!firstRegime?.coolingRegimeProductId) {
      throw new Error("Cooling regime product ID not found");
    }

    const payload: any = {
      coolingRegimeProductId: firstRegime.coolingRegimeProductId,
      shortTermMainCoolingTemperatureId:
        firstRegime.coolingTemperatureCodeShortTerm,
      longTermMainCoolingTemperatureId:
        firstRegime.coolingTemperatureCodeLongTerm,
      shortTermDisplacementMainCoolingTemperatureId:
        firstRegime.coolingTemperatureCodeShortTermDisplacement,
      longTermDisplacementMainCoolingTemperatureId:
        firstRegime.coolingTemperatureCodeLongTermDisplacement,
    };

    // Update only the changed field
    if (field === "shortTermMain") {
      payload.shortTermMainCoolingTemperatureId = value;
    } else if (field === "longTermMain") {
      payload.longTermMainCoolingTemperatureId = value;
    } else if (field === "shortTermDisplacementMain") {
      payload.shortTermDisplacementMainCoolingTemperatureId = value;
    } else if (field === "longTermDisplacementMain") {
      payload.longTermDisplacementMainCoolingTemperatureId = value;
    }

    await updateTemperaturesMutation.mutateAsync(payload);
  };

  const handleSavePriority = async (
    field: keyof PriorityUpdates,
    value: number | null,
  ) => {
    if (!firstRegime?.coolingRegimeProductId) {
      throw new Error("Cooling regime product ID not found");
    }

    const payload: any = {
      coolingRegimeProductId: firstRegime.coolingRegimeProductId,
      shortTermCoolingPriorityId: firstRegime.coolingPriorityCodeShortTerm,
      longTermCoolingPriorityId: firstRegime.coolingPriorityCodeLongTerm,
    };

    // Update only the changed field
    if (field === "shortTerm") {
      payload.shortTermCoolingPriorityId = value;
    } else if (field === "longTerm") {
      payload.longTermCoolingPriorityId = value;
    }

    await updatePrioritiesMutation.mutateAsync(payload);
  };

  const handleSaveRelativeHumidity = async (value: 'J' | 'N' | null) => {
    if (!firstRegime?.coolingRegimeProductId) {
      throw new Error("Cooling regime product ID not found");
    }

    await updatePrioritiesMutation.mutateAsync({
      coolingRegimeProductId: firstRegime.coolingRegimeProductId,
      // keep existing priorities unchanged
      shortTermCoolingPriorityId: firstRegime.coolingPriorityCodeShortTerm,
      longTermCoolingPriorityId: firstRegime.coolingPriorityCodeLongTerm,
      relativeHumidityIndication: value ?? null,
    } as any);
  };

  const handleDelete = async () => {
    if (!firstRegime?.coolingRegimeProductId) {
      throw new Error("Cooling regime product ID not found");
    }
    const confirmed = window.confirm(
      "Weet je zeker dat je dit koelregime wilt verwijderen?",
    );
    if (!confirmed) return;
    await deleteMutation.mutateAsync(firstRegime.coolingRegimeProductId);
  };

  const handleCreate = async (values: {
    productCode: number;
    coolingTemperatureCodeShortTerm?: number | null;
    coolingTemperatureCodeLongTerm?: number | null;
    coolingTemperatureCodeShortTermDisplacement?: number | null;
    coolingTemperatureCodeLongTermDisplacement?: number | null;
    coolingPriorityCodeShortTerm?: number | null;
    coolingPriorityCodeLongTerm?: number | null;
    relativeHumidityIndication?: string | null;
  }) => {
    await createMutation.mutateAsync({
      productCode: values.productCode,
      coolingTemperatureCodeShortTerm: values.coolingTemperatureCodeShortTerm ?? null,
      coolingTemperatureCodeLongTerm: values.coolingTemperatureCodeLongTerm ?? null,
      coolingTemperatureCodeShortTermDisplacement: values.coolingTemperatureCodeShortTermDisplacement ?? null,
      coolingTemperatureCodeLongTermDisplacement: values.coolingTemperatureCodeLongTermDisplacement ?? null,
      coolingPriorityCodeShortTerm: values.coolingPriorityCodeShortTerm ?? null,
      coolingPriorityCodeLongTerm: values.coolingPriorityCodeLongTerm ?? null,
      relativeHumidityIndication: values.relativeHumidityIndication ?? null,
    } as any);
  };

  // Map data to the format expected by EditableCoolingRegimeDetails
  const mappedData = firstRegime
    ? {
        code:
          typeof coolingRegimeProductGroupId === "number" && coolingRegimeProductGroupId === 0
            ? `${firstRegime.productCode} – afwijkend koelregime voor product`
            : firstRegime.productCode,
        applicationCode: null, // Not available in product cooling regime
        relativeHumidityIndication: firstRegime.relativeHumidityIndication,
        coolingTemperatureCodeShortTerm:
          firstRegime.coolingTemperatureCodeShortTerm,
        coolingTemperatureCodeLongTerm:
          firstRegime.coolingTemperatureCodeLongTerm,
        coolingTemperatureCodeShortTermDisplacement:
          firstRegime.coolingTemperatureCodeShortTermDisplacement,
        coolingTemperatureCodeLongTermDisplacement:
          firstRegime.coolingTemperatureCodeLongTermDisplacement,
        temperatureShortTerm: firstRegime.temperatureShortTerm,
        temperatureLongTerm: firstRegime.temperatureLongTerm,
        temperatureShortTermDisplacement:
          firstRegime.temperatureShortTermDisplacement,
        temperatureLongTermDisplacement:
          firstRegime.temperatureLongTermDisplacement,
        coolingPriorityCodeShortTerm: firstRegime.coolingPriorityCodeShortTerm,
        coolingPriorityCodeLongTerm: firstRegime.coolingPriorityCodeLongTerm,
        priorityShortTerm: firstRegime.priorityShortTerm,
        priorityLongTerm: firstRegime.priorityLongTerm,
        created: firstRegime.created,
        createdBy: firstRegime.createdBy,
        lastModified: firstRegime.lastModified,
        lastModifiedBy: firstRegime.lastModifiedBy,
      }
    : null;

  return (
    <>
      <EditableCoolingRegimeDetails
        data={mappedData}
        isLoading={isLoading}
        error={isError ? new Error("Failed to load cooling regime") : null}
        onSaveTemperature={handleSaveTemperature}
        onSavePriority={handleSavePriority}
        onSaveRelativeHumidity={handleSaveRelativeHumidity}
        isSaving={
          updateTemperaturesMutation.isPending ||
          updatePrioritiesMutation.isPending
        }
        codeLabel="Productcode"
        extraProductRow={(() => {
          if (typeof coolingRegimeProductGroupId === 'number' && typeof productGroupCode === 'number') {
            if (coolingRegimeProductGroupId > 0) {
              return { label: 'Productgroepcode', value: `${productGroupCode} – koelregime op productgroep` };
            }
            if (coolingRegimeProductGroupId === 0) {
              // Note moved to productcode field; keep productgroepcode visible without suffix
              return { label: 'Productgroepcode', value: `${productGroupCode}` };
            }
          }
          return undefined;
        })()}
        showPriorities={true}
        onDelete={(hasProductRegime && canMutateProduct) ? handleDelete : undefined}
        isDeleting={deleteMutation.isPending}
        canEdit={hasProductRegime && (canMutateProduct || canMutateTemperature || canMutatePriority)}
        onCreateRequested={(!hasProductRegime && productCode && canMutateProduct) ? () => openCreate() : undefined}
      />
      {(!hasProductRegime && productCode && canMutateProduct) && (
        <ProductCoolingRegimeCreateDialog
          open={isCreateOpen}
          onClose={closeCreate}
          productCode={productCode}
          initialValues={initialCreateValues}
          onSubmit={async (v) => {
            await handleCreate(v as any);
            closeCreate();
          }}
        />
      )}
    </>
  );
};
